import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './context/AuthContext';
import { AdminProvider } from './context/AdminContext';
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';

// Pages
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import VerifyEmail from './pages/VerifyEmail';
import VerifySubUserAccount from './pages/VerifySubUserAccount';
import ResendVerification from './pages/ResendVerification';
import Projects from './pages/Projects';
import PromptBuilder from './pages/PromptBuilder';
import ApiManager from './pages/ApiManager';
import KnowledgeBase from './pages/KnowledgeBase';
import ChatbotSimulator from './pages/ChatbotSimulator';
import SDKIntegration from './pages/SDKIntegration';
import Billing from './pages/Billing';
import LocationManager from './pages/LocationManager';
import ProjectOverview from './pages/ProjectOverview';
import Landing from './pages/Landing';
import Analytics from './pages/Analytics';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import About from './pages/About';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms'
import HumanAgents from './pages/HumanAgents';
import FlowBuilder from './pages/FlowBuilder';
import SubUsers from './pages/SubUsers';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import UserManagement from './pages/admin/UserManagement';
import AdminManagement from './pages/admin/AdminManagement';
import SystemSettings from './pages/admin/SystemSettings';
import UserDetails from './pages/admin/UserDetails';
import ProjectDetails from './pages/admin/ProjectDetails';
import ChangePassword from './pages/admin/ChangePassword';

// Create React Query client
const queryClient = new QueryClient();

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AdminProvider>
          <Router>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Navigate to="/login" replace />} />
              <Route path="/signup" element={<Navigate to="/login" replace />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/verify-sub-user-account" element={<VerifySubUserAccount />} />
              <Route path="/resend-verification" element={<ResendVerification />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />

              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin/dashboard"
                element={
                  <AdminProtectedRoute>
                    <AdminDashboard />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminProtectedRoute>
                    <UserManagement />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/users/:userId"
                element={
                  <AdminProtectedRoute>
                    <UserDetails />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/projects/:projectId"
                element={
                  <AdminProtectedRoute>
                    <ProjectDetails />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/admins"
                element={
                  <AdminProtectedRoute>
                    <AdminManagement />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/settings"
                element={
                  <AdminProtectedRoute>
                    <SystemSettings />
                  </AdminProtectedRoute>
                }
              />
              <Route
                path="/admin/change-password"
                element={
                  <AdminProtectedRoute>
                    <ChangePassword />
                  </AdminProtectedRoute>
                }
              />

              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects"
                element={
                  <ProtectedRoute>
                    <Projects />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId"
                element={
                  <ProtectedRoute>
                    <ProjectOverview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId/api-manager"
                element={
                  <ProtectedRoute>
                    <ApiManager />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId/flow-builder"
                element={
                  <ProtectedRoute>
                    <FlowBuilder />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId/prompt-builder"
                element={
                  <ProtectedRoute>
                    <PromptBuilder />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId/billing"
                element={
                  <ProtectedRoute>
                    <Billing />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId/knowledge-base"
                element={
                  <ProtectedRoute>
                    <KnowledgeBase />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId/simulator"
                element={
                  <ProtectedRoute>
                    <ChatbotSimulator />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId/sdk"
                element={
                  <ProtectedRoute>
                    <SDKIntegration />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId/analytics"
                element={
                  <ProtectedRoute>
                    <Analytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId/locations"
                element={
                  <ProtectedRoute>
                    <LocationManager />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId/human-agents"
                element={
                  <ProtectedRoute>
                    <HumanAgents />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sub-users"
                element={
                  <ProtectedRoute>
                    <SubUsers />
                  </ProtectedRoute>
                }
              />

              {/* Redirect root to landing page */}
              <Route path="/" element={<Landing />} />
            </Routes>
          </Router>
        </AdminProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
